import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle } from '@monigo/ui';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/keys.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { EmptyState } from '../../components/Status/EmptyState.js';

interface QueueRow {
  id: string;
  user_id: string;
  id_document_type: string;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
}

export function KycQueuePage() {
  const { t, i18n } = useTranslation();
  const queueQ = useQuery({
    queryKey: qk.kycQueue,
    queryFn: () => callFunction<{ items: QueueRow[] }>('admin-kyc-queue'),
  });

  if (queueQ.isLoading) return <LoadingScreen />;
  if (queueQ.isError) return <ErrorState onRetry={() => queueQ.refetch()} />;
  if (!queueQ.data?.items.length) return <EmptyState title={t('kyc.empty')} />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('kyc.queueTitle')}</CardTitle>
      </CardHeader>
      <ul className="flex flex-col gap-2 px-5 pb-5 text-sm">
        {queueQ.data.items.map((row) => {
          const name = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
          return (
            <li key={row.id}>
              <Link
                to={`/kyc/${row.id}`}
                className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface p-3 hover:bg-surface-elevated"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-semibold text-text">
                    {name || t('kyc.unknownCustomer')}
                  </span>
                  <span className="truncate text-xs text-text-muted">
                    {t(`kyc.docType.${row.id_document_type}`, { defaultValue: row.id_document_type })}
                  </span>
                </span>
                <span className="shrink-0 text-text-muted">
                  {new Date(row.created_at).toLocaleDateString(
                    i18n.language === 'fr' ? 'fr-FR' : 'en-US',
                  )}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
